import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Sidebar from "../../../../components/sidebar";
import { firesService } from "../../../../services/firesService";
import {
  getDetectionsFromScan,
  normalizeListResponse,
} from "../../../../utils/firmsScanDetections";
import {
  MainContainer,
  SideContainer,
  ContentWrapper,
  TopBar,
  BackButton,
  HeaderSection,
  Title,
  Subtitle,
  SummaryRow,
  SummaryCard,
  SummaryLabel,
  SummaryValue,
  ToolbarRow,
  ResultCount,
  PerPageWrap,
  PerPageLabel,
  PerPageSelect,
  TableWrap,
  StyledTable,
  Badge,
  Mono,
  Muted,
  ActionButton,
  DetailRow,
  DetailPanel,
  DetailTitle,
  DetailMeta,
  DetectionList,
  DetectionItem,
  PaginationContainer,
  PageButton,
  EmptyState,
  LoadingText,
  DesktopOnly,
  MobileCards,
  MobileCard,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardActions,
} from "./styles";

const formatScanDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
};

const getSourcesLabel = (scan) => {
  if (scan?.source) return String(scan.source);
  const meta = scan?.firms_sources_queried;
  const succeeded = meta?.succeeded;
  if (Array.isArray(succeeded) && succeeded.length) {
    return succeeded.join(", ");
  }
  const attempted = meta?.attempted;
  if (Array.isArray(attempted) && attempted.length) {
    return attempted.join(", ");
  }
  return "—";
};

const getSourcesStats = (scan) => {
  const meta = scan?.firms_sources_queried;
  if (!meta || typeof meta !== "object") return null;
  const ok = Array.isArray(meta.succeeded) ? meta.succeeded.length : null;
  const tried = Array.isArray(meta.attempted) ? meta.attempted.length : null;
  const failed = meta.failed
    ? Object.keys(meta.failed).length
    : Array.isArray(meta.failed_list)
      ? meta.failed_list.length
      : 0;
  if (ok == null && tried == null) return null;
  return { ok, tried, failed, partial: Boolean(meta.partial_failure) };
};

const FirmsScanHistory = () => {
  const { id_code, fireAreaId } = useParams();
  const navigate = useNavigate();
  const collapsed = useSelector((state) => state.sidebar);

  const [fireArea, setFireArea] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  /** @type {'all' | 'risk' | 'detections' | 'new'} */
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!fireAreaId) {
      setLoading(false);
      setFetchError("Área FIRMS não informada na URL.");
      return;
    }

    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setFetchError("");
      try {
        const [areaRes, scansRes] = await Promise.all([
          firesService.getArea(fireAreaId),
          firesService.listAreaScans(fireAreaId, 100),
        ]);
        if (cancelled) return;
        setFireArea(areaRes || null);
        setScans(normalizeListResponse(scansRes));
      } catch (err) {
        console.error("Erro ao carregar histórico FIRMS:", err);
        if (cancelled) return;
        setFireArea(null);
        setScans([]);
        setFetchError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.message ||
            "Não foi possível carregar o histórico de escaneamento."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [fireAreaId]);

  const summary = useMemo(() => {
    const total = scans.length;
    const withRisk = scans.filter((s) => s.has_risk).length;
    const detections = scans.reduce(
      (acc, s) => acc + (Number(s.detection_count) || 0),
      0
    );
    const newDetections = scans.reduce(
      (acc, s) => acc + (Number(s.new_detections_count) || 0),
      0
    );
    const lastAt = scans[0]?.scan_at || null;
    return { total, withRisk, detections, newDetections, lastAt };
  }, [scans]);

  const filteredScans = useMemo(() => {
    if (filter === "risk") return scans.filter((s) => s.has_risk);
    if (filter === "detections") {
      return scans.filter((s) => Number(s.detection_count) > 0);
    }
    if (filter === "new") {
      return scans.filter((s) => Number(s.new_detections_count) > 0);
    }
    return scans;
  }, [scans, filter]);

  const filterLabels = {
    all: "todos",
    risk: "com risco",
    detections: "com detecções",
    new: "com novos focos",
  };

  const setTableFilter = (next) => {
    setFilter((prev) => (prev === next && next !== "all" ? "all" : next));
    setCurrentPage(1);
    setExpandedId(null);
  };

  const totalPages = Math.max(1, Math.ceil(filteredScans.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredScans.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const handleBack = () => {
    if (id_code) {
      navigate(`/area_details/${id_code}`);
      return;
    }
    if (fireArea?.guardian_area) {
      navigate(`/area_details/${fireArea.guardian_area}`);
      return;
    }
    navigate("/areas");
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(
      (p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1
    )
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);

  const renderDetail = (scan) => {
    const detections = getDetectionsFromScan(scan);
    const stats = getSourcesStats(scan);
    return (
      <DetailPanel>
        <DetailTitle>
          Detalhes do scan · {detections.length} foco(s) listado(s)
        </DetailTitle>
        <DetailMeta>
          Fontes: {getSourcesLabel(scan)}
          {stats ? (
            <>
              {" "}
              · OK {stats.ok ?? "—"}/{stats.tried ?? "—"}
              {stats.failed > 0 ? ` · Falhas ${stats.failed}` : ""}
            </>
          ) : null}
        </DetailMeta>
        {scan.firms_partial_failure_summary ? (
          <DetailMeta>Falha parcial: {scan.firms_partial_failure_summary}</DetailMeta>
        ) : null}
        {scan.error_message ? (
          <DetailMeta style={{ color: "var(--danger)" }}>
            Erro: {scan.error_message}
          </DetailMeta>
        ) : null}
        {detections.length > 0 ? (
          <DetectionList>
            {detections.map((d, i) => (
              <DetectionItem key={`${scan.id}-${d.lat}-${d.lon}-${i}`}>
                <Mono>
                  {d.lat.toFixed(4)}, {d.lon.toFixed(4)}
                </Mono>
                {d.acq_date || d.acq_time
                  ? ` · ${[d.acq_date, d.acq_time].filter(Boolean).join(" ")}`
                  : ""}
                {d.satellite ? ` · Sat: ${d.satellite}` : ""}
                {d.confidence != null ? ` · Conf: ${d.confidence}` : ""}
                {d.frp != null ? ` · FRP: ${d.frp}` : ""}
                {d.firms_query_source ? ` · ${d.firms_query_source}` : ""}
              </DetectionItem>
            ))}
          </DetectionList>
        ) : (
          <DetailMeta>
            {Number(scan.detection_count) > 0
              ? "Há contagem de detecções, mas os pontos detalhados não estão disponíveis neste registro."
              : "Sem focos neste scan."}
          </DetailMeta>
        )}
      </DetailPanel>
    );
  };

  return (
    <MainContainer>
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <ContentWrapper>
          <TopBar>
            <BackButton type="button" onClick={handleBack}>
              Voltar
            </BackButton>
          </TopBar>

          <HeaderSection>
            <Title>Histórico de scans FIRMS</Title>
            <Subtitle>
              {fireArea?.name || "Área de monitoramento"}
              {fireArea?.bbox_str ? ` · Bbox ${fireArea.bbox_str}` : ""}
            </Subtitle>
          </HeaderSection>

          {loading ? (
            <LoadingText>Carregando histórico...</LoadingText>
          ) : fetchError ? (
            <EmptyState>{fetchError}</EmptyState>
          ) : (
            <>
              <SummaryRow>
                <SummaryCard
                  type="button"
                  $interactive
                  $active={filter === "all"}
                  onClick={() => setTableFilter("all")}
                  aria-pressed={filter === "all"}
                  title="Mostrar todos os scans"
                >
                  <SummaryLabel>Scans</SummaryLabel>
                  <SummaryValue>{summary.total}</SummaryValue>
                </SummaryCard>
                <SummaryCard
                  type="button"
                  $interactive
                  $active={filter === "risk"}
                  onClick={() => setTableFilter("risk")}
                  aria-pressed={filter === "risk"}
                  title="Filtrar scans com risco"
                >
                  <SummaryLabel>Com risco</SummaryLabel>
                  <SummaryValue $tone={summary.withRisk > 0 ? "danger" : "ok"}>
                    {summary.withRisk}
                  </SummaryValue>
                </SummaryCard>
                <SummaryCard
                  type="button"
                  $interactive
                  $active={filter === "detections"}
                  onClick={() => setTableFilter("detections")}
                  aria-pressed={filter === "detections"}
                  title="Filtrar scans com detecções"
                >
                  <SummaryLabel>Detecções</SummaryLabel>
                  <SummaryValue>{summary.detections}</SummaryValue>
                </SummaryCard>
                <SummaryCard
                  type="button"
                  $interactive
                  $active={filter === "new"}
                  onClick={() => setTableFilter("new")}
                  aria-pressed={filter === "new"}
                  title="Filtrar scans com novos focos"
                >
                  <SummaryLabel>Novos focos</SummaryLabel>
                  <SummaryValue>{summary.newDetections}</SummaryValue>
                </SummaryCard>
                <SummaryCard type="button" disabled aria-disabled="true">
                  <SummaryLabel>Último scan</SummaryLabel>
                  <SummaryValue style={{ fontSize: "0.95rem" }}>
                    {formatScanDate(summary.lastAt)}
                  </SummaryValue>
                </SummaryCard>
              </SummaryRow>

              {scans.length === 0 ? (
                <EmptyState>
                  Nenhum scan executado ainda para esta área FIRMS. Use
                  &quot;Executar scan&quot; nos detalhes da área para gerar o
                  primeiro resultado.
                </EmptyState>
              ) : filteredScans.length === 0 ? (
                <EmptyState>
                  Nenhum scan {filterLabels[filter]} neste histórico. Clique em
                  &quot;Scans&quot; para limpar o filtro.
                </EmptyState>
              ) : (
                <>
                  <ToolbarRow>
                    <ResultCount>
                      {filteredScans.length} scan
                      {filteredScans.length !== 1 ? "s" : ""}
                      {filter !== "all" ? ` (${filterLabels[filter]})` : ""}
                      {" · "}
                      página {currentPage} de {totalPages}
                      {filter !== "all" ? ` · de ${scans.length} no total` : ""}
                    </ResultCount>
                    <PerPageWrap>
                      <PerPageLabel>Por página</PerPageLabel>
                      <PerPageSelect
                        value={itemsPerPage}
                        aria-label="Itens por página"
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                          setExpandedId(null);
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </PerPageSelect>
                    </PerPageWrap>
                  </ToolbarRow>

                  <DesktopOnly>
                    <TableWrap>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th style={{ width: 44 }}>#</th>
                            <th>Data / hora</th>
                            <th>Status</th>
                            <th>Detecções</th>
                            <th>Novos</th>
                            <th>Janela</th>
                            <th>Fontes</th>
                            <th>Obs.</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((scan, index) => {
                            const absoluteIndex = indexOfFirstItem + index + 1;
                            const isExpanded = expandedId === scan.id;
                            const detections = getDetectionsFromScan(scan);
                            const stats = getSourcesStats(scan);
                            const obs =
                              scan.error_message ||
                              scan.firms_partial_failure_summary ||
                              null;
                            return (
                              <>
                                <tr
                                  key={scan.id}
                                  data-expanded={isExpanded ? "true" : "false"}
                                >
                                  <td>
                                    <Muted>{absoluteIndex}</Muted>
                                  </td>
                                  <td>{formatScanDate(scan.scan_at)}</td>
                                  <td>
                                    {scan.has_risk ? (
                                      <Badge $tone="danger">Risco</Badge>
                                    ) : (
                                      <Badge $tone="ok">OK</Badge>
                                    )}
                                    {scan.has_new_risk ? (
                                      <>
                                        {" "}
                                        <Badge $tone="warn">Novo risco</Badge>
                                      </>
                                    ) : null}
                                  </td>
                                  <td>
                                    <strong>{scan.detection_count ?? 0}</strong>
                                    {detections.length > 0 &&
                                    detections.length !==
                                      Number(scan.detection_count) ? (
                                      <Muted>
                                        {" "}
                                        ({detections.length} pts)
                                      </Muted>
                                    ) : null}
                                  </td>
                                  <td>{scan.new_detections_count ?? 0}</td>
                                  <td>
                                    {scan.days != null
                                      ? `${scan.days} dia(s)`
                                      : "—"}
                                  </td>
                                  <td>
                                    <Mono title={getSourcesLabel(scan)}>
                                      {String(getSourcesLabel(scan)).length > 28
                                        ? `${String(getSourcesLabel(scan)).slice(0, 28)}…`
                                        : getSourcesLabel(scan)}
                                    </Mono>
                                    {stats?.partial ? (
                                      <>
                                        {" "}
                                        <Badge $tone="warn">Parcial</Badge>
                                      </>
                                    ) : null}
                                  </td>
                                  <td>
                                    {obs ? (
                                      <Muted title={obs}>
                                        {obs.length > 36
                                          ? `${obs.slice(0, 36)}…`
                                          : obs}
                                      </Muted>
                                    ) : (
                                      <Muted>—</Muted>
                                    )}
                                  </td>
                                  <td>
                                    <ActionButton
                                      type="button"
                                      onClick={() => toggleExpand(scan.id)}
                                    >
                                      {isExpanded
                                        ? "Ocultar"
                                        : detections.length > 0
                                          ? `Focos (${detections.length})`
                                          : "Detalhes"}
                                    </ActionButton>
                                  </td>
                                </tr>
                                {isExpanded ? (
                                  <DetailRow key={`${scan.id}-detail`}>
                                    <td colSpan={9}>{renderDetail(scan)}</td>
                                  </DetailRow>
                                ) : null}
                              </>
                            );
                          })}
                        </tbody>
                      </StyledTable>
                    </TableWrap>
                  </DesktopOnly>

                  <MobileCards>
                    {currentItems.map((scan, index) => {
                      const absoluteIndex = indexOfFirstItem + index + 1;
                      const isExpanded = expandedId === scan.id;
                      const detections = getDetectionsFromScan(scan);
                      return (
                        <MobileCard key={`m-${scan.id}`}>
                          <MobileCardTitle>
                            #{absoluteIndex} · {formatScanDate(scan.scan_at)}
                          </MobileCardTitle>
                          <MobileCardMeta>
                            {scan.has_risk ? "Risco" : "OK"} ·{" "}
                            {scan.detection_count ?? 0} detecção(ões) ·{" "}
                            {scan.new_detections_count ?? 0} novo(s)
                          </MobileCardMeta>
                          <MobileCardMeta>
                            Janela:{" "}
                            {scan.days != null ? `${scan.days} dia(s)` : "—"} ·
                            Fontes: {getSourcesLabel(scan)}
                          </MobileCardMeta>
                          {scan.error_message ? (
                            <MobileCardMeta style={{ color: "var(--danger)" }}>
                              {scan.error_message}
                            </MobileCardMeta>
                          ) : null}
                          <MobileCardActions>
                            <ActionButton
                              type="button"
                              onClick={() => toggleExpand(scan.id)}
                            >
                              {isExpanded
                                ? "Ocultar"
                                : detections.length > 0
                                  ? `Focos (${detections.length})`
                                  : "Detalhes"}
                            </ActionButton>
                          </MobileCardActions>
                          {isExpanded ? renderDetail(scan) : null}
                        </MobileCard>
                      );
                    })}
                  </MobileCards>

                  {totalPages > 1 && (
                    <PaginationContainer>
                      <PageButton
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => {
                          setCurrentPage((p) => p - 1);
                          setExpandedId(null);
                        }}
                        aria-label="Página anterior"
                      >
                        <FiChevronLeft />
                      </PageButton>
                      {pageNumbers.map((p, i) =>
                        p === "..." ? (
                          <Muted key={`e-${i}`}>…</Muted>
                        ) : (
                          <PageButton
                            key={p}
                            type="button"
                            onClick={() => {
                              setCurrentPage(p);
                              setExpandedId(null);
                            }}
                            style={
                              p === currentPage
                                ? {
                                    borderColor: "var(--accent)",
                                    color: "var(--accent-secondary)",
                                    fontWeight: 700,
                                  }
                                : undefined
                            }
                          >
                            {p}
                          </PageButton>
                        )
                      )}
                      <PageButton
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          setCurrentPage((p) => p + 1);
                          setExpandedId(null);
                        }}
                        aria-label="Próxima página"
                      >
                        <FiChevronRight />
                      </PageButton>
                    </PaginationContainer>
                  )}
                </>
              )}
            </>
          )}
        </ContentWrapper>
      </SideContainer>
    </MainContainer>
  );
};

export default FirmsScanHistory;
